/**
 * Industry-standard units & criteria picker for assembling a new global template.
 */
import React, { useMemo } from "react";
import { translateUi } from "../i18n/index";
import { TRAIN_DRIVER_V1_CYCLE } from "../cycles/trainDriverV1";
import { cb, cbCn } from "./cycleBuilderTheme";
import type { WizardCriteriaItem } from "./CycleWizardStore";
import { LibraryCriteriaTree } from "./LibraryCriteriaTree";
import {
  INDUSTRY_STANDARD_LIBRARY_ID,
  collectVisiblePayloadsForUnit,
  industryUnitDisplayTitle,
  type LibraryPayload,
  type LibraryUnit,
} from "./cycleLibraryUi";

export function IndustryStandardUnitsPane({
  items,
  keyCounts,
  selectedLibrary,
  isMobile,
  isDarkMode = false,
  demoLabel,
  explainLabel,
  expandedUnits,
  expandedElements,
  onToggleUnit,
  onToggleElement,
  onToggleLine,
  onSetPayloadsSelected,
  onClearSelected,
  onAddWhole,
  onAddSelectedTo,
  onAddLine,
  onLibraryDragStart,
  onLibraryDragEnd,
  getDragLibraryItems,
  compactTiles,
  tapToAdd,
  hideHeader = false,
  horizontalTiles = false,
}: {
  items: WizardCriteriaItem[];
  keyCounts: Map<string, number>;
  selectedLibrary: Record<string, LibraryPayload>;
  isMobile?: boolean;
  isDarkMode?: boolean;
  demoLabel: string;
  explainLabel: string;
  expandedUnits: Record<string, boolean>;
  expandedElements: Record<string, boolean>;
  onToggleUnit: (key: string) => void;
  onToggleElement: (key: string) => void;
  onToggleLine: (payload: LibraryPayload) => void;
  onSetPayloadsSelected: (payloads: LibraryPayload[], selected: boolean) => void;
  onClearSelected: (keys: string[]) => void;
  onAddWhole: (payloads: LibraryPayload[]) => void;
  onAddSelectedTo: (unitPayloads: LibraryPayload[], type: "demonstrate" | "explain") => void;
  onAddLine: (payload: LibraryPayload, type: "demonstrate" | "explain") => void;
  onLibraryDragStart: (count: number) => void;
  onLibraryDragEnd: () => void;
  getDragLibraryItems: (payload: LibraryPayload) => LibraryPayload[];
  compactTiles?: boolean;
  tapToAdd?: boolean;
  hideHeader?: boolean;
  horizontalTiles?: boolean;
}) {
  const units = TRAIN_DRIVER_V1_CYCLE.units as LibraryUnit[];
  const cycleId = INDUSTRY_STANDARD_LIBRARY_ID;

  const allVisible = useMemo(() => {
    const out: LibraryPayload[] = [];
    for (const unit of units) {
      out.push(...collectVisiblePayloadsForUnit(cycleId, unit, items, keyCounts));
    }
    return out;
  }, [units, cycleId, items, keyCounts]);

  const visibleKeys = allVisible.map((p) => p.libraryKey).filter(Boolean) as string[];
  const selectedHere = visibleKeys.filter((k) => !!selectedLibrary[k]);
  const allSelected = visibleKeys.length > 0 && selectedHere.length === visibleKeys.length;

  const toggleUnitExclusive = (key: string) => {
    const opening = !(expandedUnits[key] ?? false);
    if (opening) {
      for (const unit of units) {
        const otherKey = `${cycleId}-${unit.id}`;
        if (otherKey !== key && (expandedUnits[otherKey] ?? false)) onToggleUnit(otherKey);
      }
    }
    onToggleUnit(key);
  };

  return (
    <div className="overflow-hidden" data-industry-standard-units>
      {hideHeader ? null : (
      <div className={cbCn("flex flex-wrap items-start justify-between gap-3 pb-3", isDarkMode ? "border-b border-slate-700" : "border-b border-slate-100")}>
        <div className="min-w-0">
          <h5 className={cbCn("font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
            {translateUi("Industry standard units")}
          </h5>
          <p className={cbCn("mt-0.5 text-xs sm:text-sm", cb.bodySm(isDarkMode))}>
            {translateUi("Open a unit to pick criteria, or add the whole unit in one tap.")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cbCn(
              "rounded-full px-2.5 py-1 text-xs font-medium tabular-nums",
              selectedHere.length > 0
                ? isDarkMode
                  ? "bg-indigo-950/70 text-indigo-200"
                  : "bg-indigo-100 text-indigo-800"
                : isDarkMode
                  ? "bg-slate-800 text-slate-400"
                  : "bg-slate-100 text-slate-600"
            )}
          >
            {selectedHere.length} {translateUi("selected")}
          </span>
          <button
            type="button"
            disabled={visibleKeys.length === 0}
            onClick={() => onSetPayloadsSelected(allVisible, !allSelected)}
            className={cbCn(
              "text-xs font-medium disabled:opacity-40",
              isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
            )}
          >
            {allSelected ? translateUi("Deselect all") : translateUi("Select all")}
          </button>
          {selectedHere.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => onClearSelected(visibleKeys)}
                className={cbCn("text-xs font-medium", isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}
              >
                {translateUi("Clear")}
              </button>
              <button
                type="button"
                onClick={() => onAddWhole(allVisible.filter((p) => p.libraryKey && selectedLibrary[p.libraryKey]))}
                className={cbCn(
                  "rounded-lg px-3 py-1.5 text-xs font-semibold",
                  isDarkMode ? "bg-indigo-500 text-white hover:bg-indigo-400" : "bg-indigo-600 text-white hover:bg-indigo-700"
                )}
                title={translateUi("Add ticked lines, mapped to Demonstrate or Explain")}
              >
                {translateUi("Add selected")}
              </button>
            </>
          )}
        </div>
      </div>
      )}
      {hideHeader ? (
        <div className="flex flex-wrap items-center justify-end gap-2 pb-3">
          <span
            className={cbCn(
              "rounded-full px-2.5 py-1 text-xs font-medium tabular-nums",
              selectedHere.length > 0
                ? isDarkMode
                  ? "bg-indigo-950/70 text-indigo-200"
                  : "bg-indigo-100 text-indigo-800"
                : isDarkMode
                  ? "bg-slate-800 text-slate-400"
                  : "bg-slate-100 text-slate-600"
            )}
          >
            {selectedHere.length} {translateUi("selected")}
          </span>
          <button
            type="button"
            disabled={visibleKeys.length === 0}
            onClick={() => onSetPayloadsSelected(allVisible, !allSelected)}
            className={cbCn(
              "text-xs font-medium disabled:opacity-40",
              isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
            )}
          >
            {allSelected ? translateUi("Deselect all") : translateUi("Select all")}
          </button>
        </div>
      ) : null}
      <div className={hideHeader ? "max-h-[min(42vh,32rem)] overflow-y-auto pr-1" : "pt-4"}>
        <LibraryCriteriaTree
          cycleId={cycleId}
          units={units}
          items={items}
          keyCounts={keyCounts}
          selectedLibrary={selectedLibrary}
          pickable
          variant="tiles"
          unitTitle={industryUnitDisplayTitle}
          isMobile={isMobile}
          isDarkMode={isDarkMode}
          demoLabel={demoLabel}
          explainLabel={explainLabel}
          expandedUnits={expandedUnits}
          expandedElements={expandedElements}
          onToggleUnit={toggleUnitExclusive}
          onToggleElement={onToggleElement}
          onToggleLine={onToggleLine}
          onSetPayloadsSelected={onSetPayloadsSelected}
          onClearSelected={onClearSelected}
          onAddWhole={onAddWhole}
          onAddSelectedTo={onAddSelectedTo}
          onAddLine={onAddLine}
          onLibraryDragStart={onLibraryDragStart}
          onLibraryDragEnd={onLibraryDragEnd}
          getDragLibraryItems={getDragLibraryItems}
          compactTiles={compactTiles}
          horizontalTiles={horizontalTiles || hideHeader}
          tapToAdd={tapToAdd}
        />
      </div>
    </div>
  );
}
